import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR, formatDate } from "@/components/app/Shared";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Orders() {
  const { user, showPrices } = useAuth();
  const isAdmin = ["admin", "organization_admin"].includes(user?.role);
  const canDelete = isAdmin || user?.role === "chef";
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const removeOrder = async (oid) => {
    if (confirmDeleteId !== oid) {
      setConfirmDeleteId(oid);
      setTimeout(() => setConfirmDeleteId((curr) => (curr === oid ? null : curr)), 3500);
      return;
    }
    try {
      await api.delete(`/orders/${oid}`);
      toast.success("Bestelling verwijderd");
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const filtered = orders.filter((o) => {
    if (!q) return true;
    const ql = q.toLowerCase();
    return o.restaurant_name.toLowerCase().includes(ql) ||
           o.chef_name.toLowerCase().includes(ql) ||
           (o.items || []).some((it) => it.product_name?.toLowerCase().includes(ql));
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="orders-page">
      <PageHeader
        overline="Bestellingen"
        title="Alle bestellingen"
        description={["admin", "organization_admin"].includes(user?.role) ? "Bekijk en bewerk alle bestellingen, stuur door naar leveranciers." : "Bekijk al je geplaatste bestellingen."}
        actions={user?.role !== "admin" && (
          <Link to="/nieuwe-bestelling" className="inline-flex items-center gap-2 h-10 px-4 bg-[#5BC044] hover:bg-[#4FAA3A] text-white rounded-full text-sm font-semibold" data-testid="orders-new-cta">
            <Plus size={16} /> Nieuwe bestelling
          </Link>
        )}
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Zoeken op restaurant, chef of product…"
            className="w-full h-10 pl-9 pr-3 border border-zinc-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044] bg-white"
            data-testid="orders-search"
          />
        </div>
      </div>

      <div className="card-flat overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <th className="px-4 py-3 overline">Bestelling</th>
              <th className="px-4 py-3 overline">Restaurant</th>
              <th className="px-4 py-3 overline">Chef</th>
              <th className="px-4 py-3 overline">Datum</th>
              <th className="px-4 py-3 overline">Producten</th>
              {showPrices && <th className="px-4 py-3 overline text-right">Totaal</th>}
              {canDelete && <th className="px-4 py-3"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={(showPrices ? 6 : 5) + (canDelete ? 1 : 0)} className="py-12 text-center text-zinc-500">Geen bestellingen gevonden.</td></tr>
            ) : filtered.map((o) => (
              <tr key={o.id} className="hover:bg-zinc-50" data-testid={`order-row-${o.id}`}>
                <td className="px-4 py-3">
                  <Link
                    to={`/bestellingen/${o.id}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#1B2D5C] hover:bg-[#142244] text-white rounded-full text-xs font-mono font-bold tracking-wider shadow-sm hover:shadow transition-all active:scale-95"
                    data-testid={`order-pill-${o.id}`}
                  >
                    {o.readable_id || `#${o.id.slice(0, 8)}`}
                  </Link>
                </td>
                <td className="px-4 py-3 font-medium">{o.restaurant_name}</td>
                <td className="px-4 py-3 text-zinc-600">{o.chef_name}</td>
                <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{formatDate(o.created_at)}</td>
                <td className="px-4 py-3 text-zinc-700">
                  <div className="flex flex-col gap-0.5 max-w-[360px]">
                    {(o.items || []).slice(0, 3).map((it) => (
                      <span key={it.product_id} className="text-xs truncate">
                        <span className="font-mono text-zinc-500">{it.quantity}×</span> {it.product_name}
                      </span>
                    ))}
                    {(o.items || []).length > 3 && (
                      <span className="text-xs text-zinc-400">+ {o.items.length - 3} meer</span>
                    )}
                  </div>
                </td>
                {showPrices && <td className="px-4 py-3 text-right font-mono font-semibold">{formatEUR(o.total)}</td>}
                {canDelete && (
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => removeOrder(o.id)}
                      className={`p-1 ${confirmDeleteId === o.id ? "text-white bg-red-600 rounded hover:bg-red-700" : "text-zinc-400 hover:text-red-600"}`}
                      title={confirmDeleteId === o.id ? "Klik nogmaals om te bevestigen" : "Verwijderen"}
                      data-testid={`delete-order-${o.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
